import { format } from 'date-fns';
import { transformDocumentNumber } from '@/components/custom/erp';
import { FormMode } from './../../../../../types/form';
import { isValidUUID } from '@/lib/uuid-validator';
import { toFiniteNumber } from '@/lib/formatUtils';
import { DonMuaHangInput } from '@/types/schemas';
import { MA_CHUNG_TU } from '@/constants';
import { FormFieldState } from '../hooks';

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (
  detailRows: MuaHangRecord[],
  formMode: string,
  initialDetailRows?: MuaHangRecord[]
) => {
  if (!detailRows || detailRows.length === 0) return [];

  return detailRows.map((row: MuaHangRecord, index: number) => {
    const initialRow = initialDetailRows?.find((ct: MuaHangRecord) => ct.uuid === row.uuid);
    const gia_nt = parseFloat(row.gia_nt) || 0;
    const so_luong = parseFloat(row.so_luong) || 0;

    return {
      line: index + 1,
      ...(isValidUUID(row.uuid) && formMode !== 'add' ? { uuid: row.uuid } : {}),
      sl_cl: toFiniteNumber(row.so_luong) - toFiniteNumber(initialRow?.so_luong) + toFiniteNumber(initialRow?.sl_cl),
      ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
      dvt: row.dvt || row.ma_vt_data?.dvt || '',
      so_luong,
      gia_nt,
      gia: gia_nt,
      tien_nt: parseFloat(row.tien_nt) || 0,
      tien: parseFloat(row.tien_nt) || 0,
      ngay_giao: row.ngay_giao || '',
      ma_thue: row.ma_thue_data?.uuid || '',
      thue_suat: parseFloat(row.thue_suat) || parseFloat(row.ma_thue_data?.thue_suat) || 0,
      thue_nt: parseFloat(row.thue_nt) || 0,
      ma_bp: row.ma_bp_data?.uuid || row.ma_bp || '',
      ma_vv: row.ma_vv_data?.uuid || row.ma_vv || null,
      ma_hd: row.ma_hd_data?.uuid || row.ma_hd || null,
      ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || null,
      ma_ku: row.ma_ku_data?.uuid || row.ma_ku || null,
      ma_phi: row.ma_phi_data?.uuid || row.ma_phi || null,
      ma_sp: row.ma_sp_data?.uuid || row.ma_sp || null,
      ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || null,
      ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || null,
      sl_hd: parseFloat(row.sl_hd) || 0,
      sl_pn: parseFloat(row.sl_pn) || 0,
      so_ct_dh: row.so_ct_dh || '',
      line_dh: row.line_dh || null,
      so_ct_ss1: row.so_ct_ss1 || '',
      line_ss1: row.line_ss1 || null
    };
  });
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @returns Transformed data ready for API submission
 */
export const transformBaseData = (data: MuaHangRecord, state: FormFieldState): DonMuaHangInput => {
  return {
    ma_kh: state.khachHang?.uuid || '',
    mst: state.khachHang?.mst || state.khachHang?.tax_code || data.ma_so_thue || '',
    ten_kh: data.ten_kh_thue || state.khachHang?.customer_name || state.khachHang?.ten_kh || '',
    ong_ba: data.ong_ba || state.khachHang?.contact_person || '',
    dia_chi: data.dia_chi || state.khachHang?.address || '',
    e_mail: data.e_mail || state.khachHang?.email || '',
    ma_nvmh: state.nhanVien?.uuid || state.khachHang?.sales_rep || '',
    ma_tt: state.hanThanhToan?.uuid || state.khachHang?.payment_term_data?.uuid || '',
    dien_giai: data.dien_giai || '',
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu, MA_CHUNG_TU.MUA_HANG.DON_HANG_MUA_TRONG_NUOC),
    ngay_ct: format(data.ngay_ct, 'yyyy-MM-dd') || '',
    ngay_lct: format(data.ngay_ct, 'yyyy-MM-dd') || '',
    ngay_hl: data.ngay_hl ? format(data.ngay_hl, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    so_hd: data.so_hd || '',
    ma_nt: data.ma_nt || '',
    ty_gia: parseFloat(data.ty_gia) || 1,
    status: data.status || '1',
    transfer_yn: data.transfer_yn || false,
    dien_thoai: data.dien_thoai || '',
    ma_kho: state.khoHang?.uuid || '',
    ma_pttt: state.phuongThucThanhToan?.uuid || '',
    ma_dcnh: state.diaChi?.uuid || '',
    ma_gd: data.ma_gd || '',
    ly_do_huy: data.ly_do_huy || null,
    ghi_chu: data.ghi_chu || null,
    t_so_luong: data.tong_so_luong || 0,
    t_cp_nt: data.t_cp_nt || data.tong_chi_phi || 0,
    t_cp: data.t_cp || data.tong_chi_phi || 0,
    t_tien_nt: data.t_tien_nt || data.tong_tien || 0,
    t_tien: data.tong_tien || 0,
    t_thue_nt: data.t_thue_nt || data.tong_thue || 0,
    t_thue: data.tong_thue || 0,
    t_tt_nt: data.t_tt_nt || data.tong_thanh_toan || 0,
    t_tt: data.tong_thanh_toan || 0
  };
};

/**
 * Transform complete form data including detail rows for full submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param calculatedData - Calculated data from the form
 * @returns Complete transformed data ready for API submission
 */
export const transformFormData = (
  data: MuaHangRecord,
  state: FormFieldState,
  detailRows: MuaHangRecord[] = [],
  calculatedData: MuaHangRecord,
  entityUnit: MuaHangRecord,
  formMode: string
) => {
  // Get the base form data
  const baseData = transformBaseData(data, state);

  // Transform detail
  const detail = transformDetailRows(detailRows, formMode);

  // Return complete data structure
  return {
    ...baseData,
    unit_id: entityUnit?.uuid || '',
    t_so_luong: calculatedData.t_so_luong,
    t_cp_nt: calculatedData.t_cp_nt,
    t_cp: calculatedData.t_cp,
    t_tien_nt: calculatedData.t_tien_nt,
    t_tien: calculatedData.t_tien,
    t_thue_nt: calculatedData.t_thue_nt,
    t_thue: calculatedData.t_thue,
    t_tt_nt: calculatedData.t_tt_nt,
    t_tt: calculatedData.t_tt,
    chi_tiet: detail
  };
};
